import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChatTeardrop, ShareFat, BookmarkSimple, BookOpen } from '@phosphor-icons/react';
import { formatBookName } from '../../utils/bibleBooks';
import { useVerseInteractions } from '../../hooks/useVerseInteractions';
import { ShareVerseDialog } from './ShareVerseDialog';
import type { Verse as VerseType } from '../../types';
import { useBibleStore } from '../../../../shared/store/bibleStore';

interface VerseProps {
  verse: VerseType;
  text?: string;
  showReference?: boolean;
  className?: string;
  onTellMeMore?: (verse: VerseType) => void;
  excerptPadding?: boolean;
  loadFullChapter?: boolean;
}

export function Verse({
  verse,
  text,
  showReference = true,
  className = '',
  onTellMeMore,
  excerptPadding = true,
  loadFullChapter = false
}: VerseProps) {
  const [verseText, setVerseText] = useState<string>(text || 'Loading...');
  const [isHovered, setIsHovered] = useState(false);
  const { setSelectedVerse, verseContents, getVerseContent, isLoading } = useBibleStore();

  useEffect(() => {
    let isMounted = true;

    if (text) {
      setVerseText(text);
      return;
    }

    const loadVerse = async () => {
      if (!verse?.book || !verse?.chapter || !verse?.verse) return;

      const verseKey = `${verse.book}-${verse.chapter}-${verse.verse}`;
      const content = verseContents[verseKey];

      if (content) {
        setVerseText(content);
      } else if (!isLoading) {
        try {
          const newContent = await getVerseContent(verse.book, verse.chapter, verse.verse, undefined, loadFullChapter);
          if (isMounted && newContent) {
            setVerseText(newContent);
          }
        } catch (error) {
          console.error('Error loading verse:', error);
        }
      }
    };

    loadVerse();

    return () => { isMounted = false; };
  }, [verse?.book, verse?.chapter, verse?.verse, text, isLoading, getVerseContent, loadFullChapter]);

  const reference = `${formatBookName(verse.book)} ${verse.chapter}:${verse.verse}`;
  
  if (!verse || !verse.verse) {
    return (
      <div className="relative group">
        <div className="bg-[#ECF0FF] rounded-lg p-4">
          <div className="animate-pulse h-4 bg-gray-200 rounded w-3/4" />
        </div>
      </div>
    );
  }

  const formattedText = verse.verse?.includes('-') && verseText.includes('[') 
    ? verseText.split(/\[(\d+)\]/).map((part, index) => {
        if (index % 2 === 1) {
          return (
            <React.Fragment key={part}>
              <span className="text-sm font-semibold text-[#6C71A7] mr-2">
                {part}
              </span>
            </React.Fragment>
          );
        }
        return part;
      })
    : (
      <>
        <span className="font-sans text-xs font-semibold text-[rgba(54,30,6,0.5)] mr-2">
          {verse.verse}
        </span>
        {verseText}
      </>
    );

  return (
    <div 
      className="relative group h-full flex flex-col"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex flex-col justify-between h-full">
        <div className={`${excerptPadding ? 'p-4' : ''} flex flex-col justify-between ${className}`}>
          <div 
            className="text-[#2D0067] whitespace-pre-wrap break-words font-serif text-[24px] hover:underline cursor-pointer"
            onClick={() => setSelectedVerse(verse)}
          >
            {formattedText}
          </div>
          
          {showReference && (
            <div className="mt-12">
              <div className="text-sm text-[#2D0067]">{reference}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 